// ItemPhotos: uploads (foto's of documenten) bij één schouw-item.
//
// De server is de bron van waarheid: de lijst komt uit
// GET /api/projects/:pid/photos?item_id=X en blijft dus staan na herladen of
// op een ander apparaat. Uploads die door een wegvallende verbinding mislukken
// blijven zichtbaar in een wachtrij en worden automatisch opnieuw geprobeerd
// zolang deze pagina open is.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { api } from '../api/client.js';
import { setBusy as setAppBusy } from '../versionCheck.js';

const RETRY_MS = 15000;
const isImage = (p) => (p.mime || '').startsWith('image/');
const isNetworkError = (err) => !err?.statusCode;

function fmtSize(bytes) {
    const n = Number(bytes) || 0;
    if (n >= 1024 * 1024) { return `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`; }
    return `${Math.max(1, Math.round(n / 1024))} kB`;
}

export default function ItemPhotos({
    projectId,
    itemId,
    label,
    mode = 'photo',          // 'photo' | 'file'
    accept,                  // alleen bij mode 'file', bijv. '.pdf'
    allowMultiple = true,
    prominent = false,       // grote knop + grote miniaturen (voor foto-items)
    disabled,
    onCountChange,
}) {
    const [uploads, setUploads] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [queue, setQueue] = useState([]); // File-objecten die wachten op verbinding
    const [menuAnchor, setMenuAnchor] = useState(null);
    const cameraInputRef = useRef(null);
    const libraryInputRef = useRef(null);
    const countCb = useRef(onCountChange);
    countCb.current = onCountChange;
    const queueRef = useRef(queue);
    queueRef.current = queue;
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    const reload = useCallback(async () => {
        if (!projectId || !itemId) { return; }
        try {
            const res = await api.get(`/api/projects/${projectId}/photos?item_id=${encodeURIComponent(itemId)}`);
            if (!mounted.current) { return; }
            setUploads(res?.photos || []);
            setLoaded(true);
        } catch (err) {
            if (mounted.current && !isNetworkError(err)) { setError(err?.message || 'Bijlagen laden mislukt'); }
        }
    }, [projectId, itemId]);

    useEffect(() => { setLoaded(false); setUploads([]); setQueue([]); reload(); }, [reload]);

    // Meld het aantal pas als de serverlijst bekend is (anders zou een trage
    // verbinding een ingevuld item tijdelijk als leeg markeren).
    useEffect(() => {
        if (loaded) { countCb.current?.(uploads.length + queue.length); }
    }, [loaded, uploads.length, queue.length]);

    const uploadFiles = useCallback(async (files) => {
        const waiting = [];
        let firstError = null;
        for (const f of files) {
            const fd = new FormData();
            fd.append('photos', f);
            fd.append('item_id', itemId);
            try {
                await api.postMultipart(`/api/projects/${projectId}/photos`, fd);
            } catch (err) {
                if (isNetworkError(err)) { waiting.push(f); }
                else { firstError = firstError || `${f.name}: ${err.message}`; }
            }
        }
        return { waiting, firstError };
    }, [projectId, itemId]);

    const handlePick = async (ev) => {
        let files = Array.from(ev.target.files || []);
        ev.target.value = '';
        if (!files.length) { return; }
        if (!allowMultiple) { files = files.slice(0, 1); }
        setBusy(true);
        setError(null);
        const { waiting, firstError } = await uploadFiles(files);
        if (!mounted.current) { return; }
        if (waiting.length) { setQueue(prev => [...prev, ...waiting]); }
        setError(firstError);
        await reload();
        if (mounted.current) { setBusy(false); }
    };

    // Wachtrij opnieuw proberen: bij 'online' en periodiek.
    useEffect(() => {
        if (queue.length === 0) { return undefined; }
        let running = false;
        const retry = async () => {
            if (running || queueRef.current.length === 0) { return; }
            running = true;
            const files = queueRef.current;
            const { waiting, firstError } = await uploadFiles(files);
            running = false;
            if (!mounted.current) { return; }
            setQueue(waiting);
            if (firstError) { setError(firstError); }
            if (waiting.length < files.length) { reload(); }
        };
        const timer = setInterval(retry, RETRY_MS);
        window.addEventListener('online', retry);
        return () => { clearInterval(timer); window.removeEventListener('online', retry); };
    }, [queue.length, uploadFiles, reload]);

    // Zolang hier iets onderweg is of wacht, mag de app zichzelf niet verversen.
    const occupied = busy || queue.length > 0;
    useEffect(() => {
        if (!occupied) { return undefined; }
        setAppBusy(1);
        return () => setAppBusy(-1);
    }, [occupied]);

    const closeMenu = () => setMenuAnchor(null);
    const triggerCamera = () => { closeMenu(); cameraInputRef.current?.click(); };
    const triggerLibrary = () => { closeMenu(); libraryInputRef.current?.click(); };

    const handleDelete = async (upload) => {
        if (!window.confirm(`"${upload.filename}" verwijderen?`)) { return; }
        setError(null);
        try {
            await api.del(`/api/photos/${upload.id}`);
            setUploads(prev => prev.filter(p => p.id !== upload.id));
        } catch (err) {
            setError(err?.message || 'Verwijderen mislukt');
        }
    };

    const canAdd = !disabled && (allowMultiple || uploads.length + queue.length === 0);
    const thumb = prominent ? 112 : 72;
    const images = uploads.filter(isImage);
    const documents = uploads.filter(p => !isImage(p));
    const addLabel = mode === 'file'
        ? (uploads.length ? 'Nog een bestand' : 'Bestand uploaden')
        : (uploads.length ? 'Nog een foto' : 'Foto toevoegen');

    return (
        <Box sx={{ mt: prominent ? 0 : 1 }} data-testid={`uploads-${itemId}`}>
            {label && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {label}
                </Typography>
            )}

            {documents.length > 0 && (
                <Stack spacing={0.5} sx={{ mb: 1 }}>
                    {documents.map(d => (
                        <Stack key={d.id} direction="row" spacing={1} alignItems="center">
                            <InsertDriveFileIcon fontSize="small" color="action" />
                            <Typography
                                component="a"
                                href={`/api/photos/${d.id}`}
                                variant="body2"
                                sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {d.filename}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {fmtSize(d.bytes)}
                            </Typography>
                            {!disabled && (
                                <IconButton size="small" onClick={() => handleDelete(d)} aria-label={`${d.filename} verwijderen`}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Stack>
                    ))}
                </Stack>
            )}

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center' }}>
                {images.map(p => (
                    <Box key={p.id} sx={{ position: 'relative' }}>
                        <Box
                            component="a"
                            href={`/api/photos/${p.id}`}
                            target="_blank"
                            rel="noopener"
                            sx={{
                                display: 'block', width: thumb, height: thumb, borderRadius: 1, overflow: 'hidden',
                                border: '1px solid', borderColor: 'divider', bgcolor: 'grey.100',
                            }}
                        >
                            <img
                                src={`/api/photos/${p.id}?variant=web`}
                                alt={p.filename}
                                loading="lazy"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                        </Box>
                        {!disabled && (
                            <IconButton
                                size="small"
                                onClick={() => handleDelete(p)}
                                sx={{
                                    position: 'absolute', top: -8, right: -8,
                                    bgcolor: 'background.paper', boxShadow: 1,
                                    width: 28, height: 28,
                                    '&:hover': { bgcolor: 'grey.200' },
                                }}
                                aria-label={`${p.filename} verwijderen`}
                            >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>
                ))}

                {queue.map((f, i) => (
                    <Chip
                        key={`${f.name}-${i}`}
                        color="warning"
                        variant="outlined"
                        label={`${f.name}: wacht op verbinding`}
                        onDelete={() => setQueue(prev => prev.filter((_, j) => j !== i))}
                    />
                ))}

                {canAdd && mode === 'photo' && (
                    prominent ? (
                        <Button
                            variant="outlined"
                            size="large"
                            disabled={busy}
                            onClick={(ev) => setMenuAnchor(ev.currentTarget)}
                            startIcon={busy ? <CircularProgress size={18} /> : <AddAPhotoIcon />}
                        >
                            {busy ? 'Uploaden…' : addLabel}
                        </Button>
                    ) : (
                        <Tooltip title={addLabel}>
                            <span>
                                <IconButton
                                    disabled={busy}
                                    onClick={(ev) => setMenuAnchor(ev.currentTarget)}
                                    sx={{
                                        width: thumb, height: thumb,
                                        border: '1px dashed', borderColor: 'divider', borderRadius: 1,
                                        color: 'text.secondary',
                                        '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                                    }}
                                    aria-label={addLabel}
                                >
                                    {busy ? <CircularProgress size={20} /> : <AddAPhotoIcon />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )
                )}

                {canAdd && mode === 'file' && (
                    <Button
                        variant="outlined"
                        disabled={busy}
                        onClick={() => libraryInputRef.current?.click()}
                        startIcon={busy ? <CircularProgress size={18} /> : <UploadFileIcon />}
                    >
                        {busy ? 'Uploaden…' : addLabel}
                    </Button>
                )}
            </Stack>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <MenuItem onClick={triggerCamera}>
                    <ListItemIcon><CameraAltIcon fontSize="small" /></ListItemIcon>
                    Foto maken
                </MenuItem>
                <MenuItem onClick={triggerLibrary}>
                    <ListItemIcon><PhotoLibraryIcon fontSize="small" /></ListItemIcon>
                    Uit bibliotheek
                </MenuItem>
            </Menu>
            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={handlePick}
                data-testid={`camera-${itemId}`}
            />
            <input
                ref={libraryInputRef}
                type="file"
                accept={mode === 'file' ? `${accept || '.pdf'},image/*` : 'image/*'}
                multiple={allowMultiple}
                hidden
                onChange={handlePick}
                data-testid={`file-${itemId}`}
            />

            {queue.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    Geen verbinding: {queue.length === 1 ? 'dit bestand is' : 'deze bestanden zijn'} nog niet geupload.
                    Houd deze pagina open; zodra er weer verbinding is gaat het vanzelf.
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
        </Box>
    );
}
